import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PortfolioCharts from '../../components/grow/PortfolioCharts';
import PortfolioDetailsTable from '../../components/grow/PortfolioDetailsTable';
import PortfolioKpiRow from '../../components/grow/PortfolioKpiRow';
import { buildPortfolioSnapshot } from '../../services/portfolio/buildPortfolioSnapshot';
import { formatUsdcFromBaseUnits, scanBeltLifetimeDeposits } from '../../services/portfolio/beltLifetimeDeposits';

const formatUsd = (value) => {
  if (!Number.isFinite(value)) return '—';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

function getEthers() {
  return window?.ethers || null;
}

function getConfigBelts() {
  return window.CONFIG?.BELT_VAULTS || [];
}

async function getConnectedAddress() {
  if (window.currentAddressFull) return window.currentAddressFull;
  if (!window.ethereum?.request) return '';
  const accounts = await window.ethereum.request({ method: 'eth_accounts' });
  return accounts?.[0] || '';
}

async function getArbitrumProvider() {
  const ethers = getEthers();
  const rpc = window.CONFIG?.RPC?.[42161];
  if (!ethers || !rpc) return null;
  if (window.ethereum?.request) {
    try {
      const raw = await window.ethereum.request({ method: 'eth_chainId' });
      const chainId = typeof raw === 'string' && raw.startsWith('0x')
        ? parseInt(raw, 16)
        : Number(raw);
      if (chainId === 42161) {
        return new ethers.BrowserProvider(window.ethereum, 'any');
      }
    } catch {
      // fall back to RPC provider
    }
  }
  return new ethers.JsonRpcProvider(rpc, 42161);
}

export default function GrowPortfolioPage() {
  const [address, setAddress] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [beltDeposits, setBeltDeposits] = useState({});
  const [beltProgress, setBeltProgress] = useState({ done: 0, total: 0, status: {} });
  const [refreshTick, setRefreshTick] = useState(0);
  const [belts, setBelts] = useState([]);

  useEffect(() => {
    let mounted = true;
    const update = async () => {
      const next = await getConnectedAddress();
      if (mounted) setAddress(next || '');
    };
    update();

    const handler = () => update();
    document.addEventListener('wallet-updated', handler);
    window.ethereum?.on?.('accountsChanged', handler);

    return () => {
      mounted = false;
      document.removeEventListener('wallet-updated', handler);
      window.ethereum?.removeListener?.('accountsChanged', handler);
    };
  }, []);

  useEffect(() => {
    setBelts(getConfigBelts());
  }, [refreshTick]);

  const refresh = useCallback(() => {
    setRefreshTick((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!address) {
      setSnapshot(null);
      setWarnings([]);
      setBeltDeposits({});
      return;
    }

    let mounted = true;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setWarnings([]);
      setBeltDeposits({});
      const beltList = getConfigBelts();
      setBelts(beltList);
      setBeltProgress({ done: 0, total: beltList.length, status: {} });

      try {
        const { snapshot: nextSnapshot, warnings: nextWarnings } = await buildPortfolioSnapshot({
          userAddress: address,
        });
        if (!mounted || controller.signal.aborted) return;
        setSnapshot(nextSnapshot);
        setWarnings(nextWarnings || []);
      } catch (err) {
        if (!mounted || controller.signal.aborted) return;
        setWarnings([err?.message || 'Failed to load portfolio snapshot.']);
      } finally {
        if (mounted) setLoading(false);
      }

      const provider = await getArbitrumProvider();
      if (!provider || !beltList.length) return;

      try {
        await scanBeltLifetimeDeposits({
          belts: beltList,
          provider,
          userAddress: address,
          signal: controller.signal,
          onProgress: (update) => {
            if (!mounted) return;
            setBeltProgress((prev) => {
              const nextStatus = { ...prev.status, [update.key]: update.status };
              const done = Object.values(nextStatus).filter((v) => ['done', 'cached', 'missing_deploy_block'].includes(v)).length;
              return { ...prev, status: nextStatus, done };
            });
            if (update.lifetimeDepositedAssets) {
              setBeltDeposits((prev) => ({
                ...prev,
                [update.key]: update,
              }));
            }
          },
        }).then((results) => {
          if (!mounted) return;
          setBeltDeposits((prev) => ({ ...prev, ...results }));
        });
      } catch (err) {
        if (!mounted) return;
        setWarnings((prev) => [...prev, err?.message || 'Belt scan failed.']);
      }
    };

    load();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [address, belts, refreshTick]);

  const allocationData = useMemo(() => {
    const buckets = snapshot?.perBucketUsd || {};
    return [
      { name: 'Wallet', value: Number(buckets.wallet || 0) },
      { name: 'Aave', value: Number(buckets.aave || 0) },
      { name: 'Lido', value: Number(buckets.lido || 0) },
      { name: 'Belts', value: Number(buckets.belts || 0) },
    ];
  }, [snapshot]);

  const chainData = useMemo(() => {
    const perChain = snapshot?.perChainUsd || {};
    return [
      { name: 'Ethereum', value: Number(perChain.ethereum || 0) },
      { name: 'Arbitrum', value: Number(perChain.arbitrum || 0) },
    ];
  }, [snapshot]);

  if (!address) {
    return (
      <section className="card" style={{ marginTop: '0.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0' }}>Portfolio</h3>
        <p className="muted small">Connect your wallet to view your portfolio summary.</p>
        <button
          className="btn"
          type="button"
          onClick={() => {
            if (window.connectSmart) {
              window.connectSmart(true);
              return;
            }
            window.ethereum?.request?.({ method: 'eth_requestAccounts' });
          }}
        >
          Connect Wallet
        </button>
      </section>
    );
  }

  return (
    <section style={{ display: 'grid', gap: '1rem', marginTop: '0.5rem' }}>
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div>
          <h3 style={{ margin: 0 }}>Portfolio</h3>
          <div className="muted small">Snapshot across wallet, protocols, and belts.</div>
        </div>
        <button className="btn btn-outline" type="button" onClick={refresh} disabled={loading}>
          Refresh
        </button>
      </div>

      {warnings.length ? (
        <div className="card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <div className="muted small">{warnings.join(' ')}</div>
        </div>
      ) : null}

      {loading && !snapshot ? (
        <div className="card">
          <div className="muted small">Loading portfolio snapshot…</div>
        </div>
      ) : null}

      {snapshot ? (
        <>
          <PortfolioKpiRow totalUsd={snapshot.totalUsd} perChainUsd={snapshot.perChainUsd} />
          <PortfolioCharts allocationData={allocationData} chainData={chainData} />

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <div className="muted small">Lifetime Deposited (Belts)</div>
                <div className="small">Gross deposits by belt owner.</div>
              </div>
              <div className="muted small">
                {beltProgress.total ? `Scanning ${beltProgress.done}/${beltProgress.total}` : 'No belts configured'}
              </div>
            </div>
            <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
              {belts.length ? belts.map((belt) => {
                const data = beltDeposits[belt.key];
                const status = data?.status || beltProgress.status[belt.key] || 'pending';
                const ready = ['done', 'cached'].includes(status);
                const amount = ready && data?.lifetimeDepositedAssets
                  ? formatUsdcFromBaseUnits(data.lifetimeDepositedAssets)
                  : null;
                return (
                  <div key={belt.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{belt.label || belt.key}</div>
                      <div className="muted small">{status === 'missing_deploy_block' ? 'Deploy block missing' : `Status: ${status}`}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div>{amount == null ? '—' : formatUsd(amount)}</div>
                      <div className="muted small">USDC</div>
                    </div>
                  </div>
                );
              }) : (
                <div className="muted small">No belt vaults configured.</div>
              )}
            </div>
          </div>

          <PortfolioDetailsTable positions={snapshot.positions} unpriced={snapshot.unpriced} />
        </>
      ) : null}
    </section>
  );
}
